import React, { useContext, useMemo, useState, useCallback } from "react";
import styles from "./Recurring.module.css";
import { SummaryStatCard } from "./components/SummaryStatCard";
import { PaymentsTable } from "./components/PaymentsTable";
import AddPaymentModal from "./components/AddPaymentModal";
import AddPaymentPlanModal from "./components/AddPaymentPlanModal";
import { DetailsPanel } from "./components/DetailsPanel";
import { Box, Stack, Typography, Button, Grid } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";

import AuthContext from "../../context/AuthContext/AuthContext";
import api_endpoints from "../../api";
import { useApiClientQuery } from "../../hooks/useApiClient";
import { useCustomTranslation } from "../../hooks/useCustomTranslation";
import { useSuccessAlert } from "../../context/SuccessAlertContext/SuccessAlertContext";

/** Map backend subscription → table row */
function mapSubToRow(s) {
  const snap = s?.payment_method_snapshot || null;

  let paymentMethod = null;
  if (snap?.kind === "card") {
    paymentMethod = {
      type: "card",
      brand: snap.brand || "Card",
      last4: snap.last4 || "",
    };
  } else if (snap?.kind === "bank") {
    paymentMethod = {
      type: "ach",
      bankName: snap.bank_name || "Bank",
      last4: snap.last4 || "",
    };
  }

  // If donor_id is populated (object), build a readable display
  const donorObj = s && s.donor_id && typeof s.donor_id === "object" ? s.donor_id : null;
  const donorDisplay =
    donorObj
      ? [
          [donorObj.first_name, donorObj.last_name].filter(Boolean).join(" ") || donorObj.email || "",
          donorObj.company ? `· ${donorObj.company}` : "",
        ].join(" ").trim()
      : "";

  return {
    id: s._id,
    customerId: donorObj?._id || s.donor_id,
    customerName: s.donor_name || donorDisplay || "", // fallback chain
    paymentMethod,
    amount: Number(s.amount),
    frequency: s.frequency,
    startDate: s.start_date ? new Date(s.start_date).toISOString().slice(0, 10) : "",
    endDate: s.end_date ? new Date(s.end_date).toISOString().slice(0, 10) : "",
    // NEW: keep next_run_at if backend provides it (not required for the card anymore)
    nextRunAt: s.next_run_at ? new Date(s.next_run_at).toISOString() : "",
    reason: s.reason || "",
    paymentsCount: Number(s.total_successful || 0),
    status: s.status || "active",
  };
}

// Simple client-side fallback for next date if API returns nothing
function computeNextLocal(rows) {
  const candidates = rows
    .map(r => ({
      name: r.customerName,
      // Prefer nextRunAt if available; else fallback to startDate (may be old)
      date: r.nextRunAt ? new Date(r.nextRunAt) : (r.startDate ? new Date(r.startDate) : null),
      amount: r.amount,
      count: 1,
    }))
    .filter(x => x.date && !Number.isNaN(x.date.getTime()));

  if (!candidates.length) return null;

  // Pick the earliest future date; if none in future, earliest overall
  const now = new Date();
  const future = candidates.filter(c => c.date > now).sort((a, b) => a.date - b.date);
  const pick = (future[0] || candidates.sort((a, b) => a.date - b.date)[0]);
  return pick ? { date: pick.date, amount: pick.amount, count: pick.count ?? 1 } : null;
}

export default function Recurring() {
  const auth = useContext(AuthContext);
  const orgId = auth?.authContext?.accessToken?.organization_id ?? undefined;
  const { t } = useCustomTranslation("Recurring Payment");
  const { setShowSuccessAlert, setMessageSuccessAlert, setShowWarningAlert, setMessageWarningAlert } = useSuccessAlert();

  // Local rows as fallback while API loads
  const [localRows, setLocalRows] = useState([
    {
      id: "r1",
      customerId: "c1",
      customerName: "Bob Test",
      paymentMethod: { type: "card", brand: "Visa", last4: "4242" },
      amount: 50,
      frequency: "Monthly",
      startDate: "2025-01-15",
      endDate: "2025-12-15",
      reason: "Campaign A",
      paymentsCount: 5,
      status: "active",
    },
    {
      id: "r2",
      customerId: "c2",
      customerName: "Alice Johnson",
      paymentMethod: { type: "ach", bankName: "Community Bank", last4: "6789" },
      amount: 25,
      frequency: "Weekly",
      startDate: "2025-02-01",
      reason: "General Donation",
      paymentsCount: 9,
      status: "active",
    },
    {
      id: "r3",
      customerId: "c3",
      customerName: "Acme Corp",
      paymentMethod: { type: "card", brand: "Mastercard", last4: "9012" },
      amount: 100,
      frequency: "Quarterly",
      startDate: "2025-01-01",
      reason: "Sponsorship",
      paymentsCount: 2,
      status: "active",
    },
  ]);

  const [isAddOpen, setIsAddOpen] = useState(false);
  const [isAddPlanOpen, setIsAddPlanOpen] = useState(false);
  const [selectedCustomerId, setSelectedCustomerId] = useState(null);
  const [detailsRefreshKey, setDetailsRefreshKey] = useState(0);

  // Fetch subscriptions (table)
  const subsQuery = useApiClientQuery(
    () => api_endpoints.listRecurringSubscriptions(orgId, { limit: 500 }),
    {
      queryKey: ["recurring-subscriptions", orgId],
      enabled: Boolean(orgId),
      staleTime: 30_000,
    }
  );

  // Fetch next upcoming payment (summary card)
  const nextQuery = useApiClientQuery(
    () => api_endpoints.getNextRecurringPayment(orgId),
    {
      queryKey: ["recurring-next", orgId],
      enabled: Boolean(orgId),
      staleTime: 15_000,
    }
  );

  // Prefer API rows; fallback to local
  const rows = useMemo(() => {
    if (subsQuery.isSuccess && Array.isArray(subsQuery.data)) {
      return subsQuery.data.map(mapSubToRow);
    }
    return localRows;
  }, [subsQuery.isSuccess, subsQuery.data, localRows]);

  const summary = useMemo(() => {
    const customersQty = new Set(rows.map((r) => r.customerId)).size;
    const amountYTD = rows.reduce(
      (sum, r) => sum + Number(r.amount || 0) * Number(r.paymentsCount || 0),
      0
    );

    // Prefer backend-computed next
    const fromApi = nextQuery?.data?.next || null;
    const noValue = t("no_value");
    const formatLabel = (date, amount, count = 1) => {
      if (!date) return noValue;
      const amountDisplay = `$${Number(amount || 0).toFixed(2)}`;
      const dateDisplay = new Date(date).toLocaleDateString();
      const customersDisplay = t("customer_count_label", { count });
      return t("next_payment_label", {
        date: dateDisplay,
        amount: amountDisplay,
        customers: customersDisplay,
      });
    };

    let nextLabel = noValue;
    if (fromApi && fromApi.date) {
      const count = Number(fromApi.count ?? 1);
      nextLabel = formatLabel(fromApi.date, fromApi.amount ?? 0, count);
    } else {
      // Fallback to local approximation
      const local = computeNextLocal(rows);
      if (local?.date) {
        nextLabel = formatLabel(local.date, local.amount ?? 0, local.count ?? 1);
      }
    }

    return { customersQty, amountYTD, nextLabel };
  }, [rows, nextQuery?.data, t]);

  const { refetch: refetchSubs } = subsQuery;
  const { refetch: refetchNext } = nextQuery;

  const refreshDetails = useCallback(() => {
    refetchSubs?.();
    refetchNext?.();
    setDetailsRefreshKey((key) => key + 1);
  }, [refetchSubs, refetchNext]);

  const handlePause = useCallback(
    async (row) => {
      if (!row?.id) return;
      try {
        await api_endpoints.pauseRecurringSubscription(row.id);
        setMessageSuccessAlert(t("subscription_paused_successfully"));
        setShowSuccessAlert(true);
      } catch (err) {
        console.error("Failed to pause subscription", err);
        setMessageWarningAlert(t("failed_to_pause_subscription"));
        setShowWarningAlert(true);
      } finally {
        refreshDetails();
      }
    },
    [refreshDetails, setMessageSuccessAlert, setShowSuccessAlert, setMessageWarningAlert, setShowWarningAlert, t]
  );

  const handleResume = useCallback(
    async (row) => {
      if (!row?.id) return;
      try {
        await api_endpoints.resumeRecurringSubscription(row.id);
        setMessageSuccessAlert(t("subscription_resumed_successfully"));
        setShowSuccessAlert(true);
      } catch (err) {
        console.error("Failed to resume subscription", err);
        setMessageWarningAlert(t("failed_to_resume_subscription"));
        setShowWarningAlert(true);
      } finally {
        refreshDetails();
      }
    },
    [refreshDetails, setMessageSuccessAlert, setShowSuccessAlert, setMessageWarningAlert, setShowWarningAlert, t]
  );

  const handleCancel = useCallback(
    async (row) => {
      if (!row?.id) return;
      try {
        await api_endpoints.cancelRecurringSubscription(row.id);
        setMessageSuccessAlert(t("subscription_cancelled_successfully"));
        setShowSuccessAlert(true);
      } catch (err) {
        console.error("Failed to cancel subscription", err);
        setMessageWarningAlert(t("failed_to_cancel_subscription"));
        setShowWarningAlert(true);
      } finally {
        refreshDetails();
      }
    },
    [refreshDetails, setMessageSuccessAlert, setShowSuccessAlert, setMessageWarningAlert, setShowWarningAlert, t]
  );

  const handleEdit = useCallback(
    async (row, patch) => {
      if (!row?.id) return;
      try {
        await api_endpoints.updateRecurringSubscription(row.id, patch);
        setMessageSuccessAlert(t("subscription_updated_successfully"));
        setShowSuccessAlert(true);
      } catch (err) {
        console.error("Failed to update subscription", err);
        setMessageWarningAlert(t("failed_to_update_subscription"));
        setShowWarningAlert(true);
      } finally {
        refreshDetails();
      }
    },
    [refreshDetails, setMessageSuccessAlert, setShowSuccessAlert, setMessageWarningAlert, setShowWarningAlert, t]
  );

  return (
    <Box className={styles.main_container}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Box>
          <Typography variant="h5" className={styles.titleGradient}>
            {t("recurring_title")}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t("recurring_subtitle")}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" onClick={() => setIsAddPlanOpen(true)}>
            {t("payment_plans_button")}
          </Button>
          <Button startIcon={<AddIcon />} variant="contained" onClick={() => setIsAddOpen(true)}>
            {t("add_payment_button")}
          </Button>
        </Stack>
      </Stack>

      <Grid container spacing={2} sx={{ mb: 1 }}>
        <Grid item xs={12} md={4}>
          <SummaryStatCard label={t("summary_customers_label")}
                           value={summary.customersQty} />
        </Grid>
        <Grid item xs={12} md={4}>
          <SummaryStatCard label={t("summary_amount_label")} value={`$${summary.amountYTD.toLocaleString()}`} />
        </Grid>
        <Grid item xs={12} md={4}>
          <SummaryStatCard
            label={t("summary_next_label")}
            value={nextQuery.isLoading ? t("loading") : summary.nextLabel}
          />
        </Grid>
      </Grid>

      <Box sx={{ mt: 2 }}>
        <PaymentsTable
          rows={rows}
          loading={subsQuery.isLoading || subsQuery.isFetching}
          onRowClick={(id) => setSelectedCustomerId(id)}
        />
      </Box>

      {isAddOpen && (
        <AddPaymentModal
          organizationId={orgId}
          onClose={() => setIsAddOpen(false)}
          onAdd={(newRow) => {
            setLocalRows((r) => [newRow, ...r]); // optimistic
            setIsAddOpen(false);
            refreshDetails();
          }}
        />
      )}

      {isAddPlanOpen && (
        <AddPaymentPlanModal
          organizationId={orgId}
          open={isAddPlanOpen}
          onClose={() => setIsAddPlanOpen(false)}
          onCreated={() => {
            setIsAddPlanOpen(false);
            refreshDetails();
          }}
        />
      )}

      <DetailsPanel
        open={!!selectedCustomerId}
        customerId={selectedCustomerId}
        rows={rows}
        organizationId={orgId}
        refreshToken={detailsRefreshKey}
        onClose={() => setSelectedCustomerId(null)}
        onPause={handlePause}
        onResume={handleResume}
        onEdit={handleEdit}
        onCancel={handleCancel}
      />
    </Box>
  );
}
